/**
 * Single-stack root navigator for Rinsepay.
 *
 * All 77+ screens live in one flat Stack.  Section chrome (admin
 * sidebar, owner NavBanner, user NavBanner, auth guards) is applied by
 * wrapping the screen's component in the appropriate *Shell at
 * registration time, based on the screen name prefix.
 *
 * See src/navigation/screens.ts for the source of truth for the screen
 * list, and src/layouts/{Admin,Owner,User}Shell for the chrome logic
 * that used to live in app/*_layout.tsx.
 */
import React, { ComponentType, useMemo } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { useTheme } from '../context/ThemeContext';
import { SCREENS, ScreenEntry } from './screens';
import AdminShell from '../layouts/AdminShell';
import OwnerShell from '../layouts/OwnerShell';
import UserShell from '../layouts/UserShell';
import ContentContainer from '../components/ContentContainer';

const AUTH_ROUTES = new Set([
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'verify-2fa',
  'verify-email',
  'index',
]);

const Stack = createNativeStackNavigator();

/**
 * Pick the right shell wrapper for a screen based on its name and
 * return a wrapped component that renders the base screen inside it.
 * We memo the wrapped component per entry so react-navigation doesn't
 * re-render the whole tree when screenOptions change.
 */
function wrapScreen(entry: ScreenEntry): ComponentType<any> {
  const Inner = entry.component;
  if (entry.name.startsWith('admin/') || entry.name === 'admin/index') {
    return function AdminWrapped(props: any) {
      return (
        <AdminShell>
          <Inner {...props} />
        </AdminShell>
      );
    };
  }
  if (entry.name.startsWith('owner/') || entry.name === 'owner/index') {
    return function OwnerWrapped(props: any) {
      return (
        <OwnerShell>
          <Inner {...props} />
        </OwnerShell>
      );
    };
  }
  if (entry.name.startsWith('user/') || entry.name === 'user/index') {
    return function UserWrapped(props: any) {
      return (
        <UserShell>
          <Inner {...props} />
        </UserShell>
      );
    };
  }
  if (AUTH_ROUTES.has(entry.name)) {
    return function AuthWrapped(props: any) {
      return (
        // footer=false: auth screens carry their own inner ContentContainer
        // (inside their ScrollView), which renders the PageFooter at the
        // scroll end — a second one here would pin below the scroll.
        <ContentContainer role="auth" footer={false}>
          <Inner {...props} />
        </ContentContainer>
      );
    };
  }
  return Inner;
}

// Pre-wrap every screen once at module load so each Stack.Screen below
// can reference a stable component reference.  (Wrapping inside render
// would create a new component per render and break navigation state.)
const WRAPPED: Array<ScreenEntry & { wrapped: ComponentType<any> }> = SCREENS.map(
  (s) => ({ ...s, wrapped: wrapScreen(s) }),
);

export function RootNavigator() {
  const { colors } = useTheme();

  const screenOptions: NativeStackNavigationOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: colors.surface },
      headerTintColor: colors.text,
      headerTitleStyle: { fontWeight: '600' },
      headerShadowVisible: false,
      contentStyle: { backgroundColor: colors.background },
      headerBackTitle: 'Back',
    }),
    [colors],
  );

  return (
    <Stack.Navigator initialRouteName="index" screenOptions={screenOptions}>
      {WRAPPED.map((s) => {
        // Owner/user screens render their own back header (SubHeader) inside
        // the shell, BELOW the NavBanner toolbar — so suppress the native
        // stack header (which would otherwise sit above everything).
        const ownsHeader = s.name.startsWith('owner/') || s.name.startsWith('user/');
        return (
          <Stack.Screen
            key={s.name}
            name={s.name}
            component={s.wrapped}
            options={{
              title: s.title,
              headerShown: ownsHeader ? false : s.headerShown !== false,
            }}
          />
        );
      })}
    </Stack.Navigator>
  );
}
